import { useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Plus } from "untitledui-js-base";
import { useMedia } from "shared/providers";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "types/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";

interface LayoutFooterProps {
  unit?: Unit;
  propertyId?: string;
}

export const LayoutFooter = ({ unit, propertyId }: LayoutFooterProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const { type } = useAuthContext();
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const [showUnlistedConfirm, setShowUnlistedConfirm] = useState(false);

  const isDisabled = unit?.listed ? !unit?.magic_link?.active : false;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        unit?.magic_link?.application_link || ""
      );
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleCopyClick = () => {
    if (!unit?.listed) {
      setShowUnlistedConfirm(true);
      return;
    }
    copyLink();
  };

  return (
    <>
      <Show if={isMobile && type.isBoomScreen}>
        <div className="flex items-center gap-2 p-4 border-t border-alpha-100">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={handleCopyClick}
            className="md:ml-6 w-full sm:w-auto"
            disabled={isDisabled}
            tooltip={
              isDisabled
                ? `When a ${dictionary.unit} is not accepting applications, applicants can only be invited via email`
                : undefined
            }
            tooltipPlacement="bottom-start"
          >
            Copy application link
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={() => setShowInviteApplicantsModal(true)}
            className="w-full sm:w-auto"
            Icon={Plus}
          >
            Invite applicants
          </Button>
        </div>
      </Show>

      {showInviteApplicantsModal && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          unitId={unit?.id}
          unitName={unit?.title}
          propertyName={unit?.property.name}
          propertyId={Number(propertyId)}
          onInviteRefreshKey={["APPLICANTS_TABLE"]}
        />
      )}

      <ConfirmDialog
        opened={showUnlistedConfirm}
        onClose={() => setShowUnlistedConfirm(false)}
        onConfirm={() => {
          copyLink();
          setShowUnlistedConfirm(false);
        }}
        title={`${dictionary.Unit} not listed`}
        description={`This ${dictionary.unit} isn't listed yet. The link you copied won't work for applicants — for internal use only.`}
        variant="primary"
        icon={{ icon: AlertCircleIcon, variant: "primary" }}
        confirmButton={{ label: "Copy link" }}
      />
    </>
  );
};
